import { Fetcher, getParam, StrictFetcher } from '@this/src/util';
/* eslint-disable max-lines */
import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import type { RouteComponentProps } from 'react-router-dom';

import Box from '@material-ui/core/Box';
import _ from 'lodash';
import type { Moment } from 'moment';
import moment from 'moment';

import ContentBody from '@this/components/shared/atoms/content_body';
import { media } from '@this/components/shared/atoms/media';
import { Loading } from '@this/components/shared/ui/feedbacks/loading/loading';
import DraftSearch from '@this/components/trips_management/trips/draft_search/draft_search';
import { styled } from '@this/constants/themes';
import type { AvailableRepository } from '@this/domain/available_repository';
import type { TripResponseArgs } from '@this/domain/trip/trip2';
import Trip, { convertTripResponseToArgs } from '@this/domain/trip/trip2';
import type User from '@this/domain/user/user';
import DatetimePicker from '@this/shared/datetime_picker/datetime_picker';
import { Modal, ModalHeader, ModalBody } from '@this/shared/ui/feedbacks/modal';
import { Button } from '@this/shared/ui/inputs/button';
import { Pagination } from '@this/shared/ui/navigations/pagination';

import type { ActivitySelected } from '@this/domain/trip/trip_activity_store';
import { Checkbox } from '@this/src/components/shared/ui/inputs/checkbox';
import TripReceiptRow from '@this/src/components/trips_management/receipts/trip_receipt_row';
import TripHeader from './trip_header';
import TripRow from './trip_row';

type SortKey = 'created_at' | 'started_at';
type SortDirection = 'asc' | 'desc';
type Type = 'user';

// ポーリング間隔
const POLLING_INTERVAL = 10000;

interface Props extends RouteComponentProps {
  user: User | null;
  serviceId: number;
  selectedKey: ActivitySelected;
  availableRepos: AvailableRepository[];
  availableOptions: string[];
}

interface FetchTripsResult {
  user: any;
  trips: TripResponseArgs[];
  show_fee: boolean;
  current_page: number;
  total_pages: number;
  in_progress_trip_ids?: string[];
}

interface TripParams {
  type: Type;
  page: number;
  trip_id: string | null | undefined;
  trip_from_date: string | undefined;
  trip_to_date: string | undefined;
  sort_key: SortKey;
  sort_direction: SortDirection;
  simple?: string;
}

const getTripIdParam = () => {
  return getParam('trip_id') ?? null;
};

const getFromDateParam = () => {
  const fromDate = getParam('trip_from_date');
  return fromDate ? moment(fromDate) : undefined;
};

const getToDateParam = () => {
  const toDate = getParam('trip_to_date');
  return toDate ? moment(toDate) : undefined;
};

const getSortKeyParam = () => {
  return getParam('sort_key') as SortKey | undefined;
};

const getSortDirectionParam = () => {
  return getParam('sort_direction') as SortDirection | undefined;
};

const getPageParam = () => {
  return parseInt(getParam('page') || '1', 10);
};

const Trips: React.FC<Props> = observer(({ user, availableRepos, availableOptions, selectedKey }) => {
  const history = useHistory();
  const tripReportAvailable = availableOptions.includes('trip_report');
  const tripReceiptAvailable = availableOptions.includes('receipt');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDraft, setSelectedDraft] = useState<any | null>(null);
  const [ongoingTrips, setOngoingTrips] = useState<Trip[]>([]);
  const [currentPage, setCurrentPage] = useState(getPageParam());
  const [totalPages, setTotalPages] = useState(1);
  const [tripId, setTripId] = useState<string | null>(getTripIdParam());
  const [tripFromDate, setTripFromDate] = useState<Moment | undefined>(() => {
    const paramDate = getFromDateParam();
    // 領収書タブでURLパラメータがない場合は、2ヶ月前をデフォルト値として設定
    if (selectedKey === 'receipt' && !paramDate && !getParam('trip_from_date')) {
      const defaultFromDate = moment().subtract(2, 'months').startOf('day');
      return defaultFromDate;
    }
    return paramDate;
  });

  const [tripToDate, setTripToDate] = useState<Moment | undefined>(() => {
    const paramDate = getToDateParam();
    // 領収書タブでURLパラメータがない場合は、本日をデフォルト値として設定
    if (selectedKey === 'receipt' && !paramDate && !getParam('trip_to_date')) {
      const defaultToDate = moment().endOf('day');
      return defaultToDate;
    }
    return paramDate;
  });

  const [sortKey, setSortKey] = useState<SortKey>(getSortKeyParam() ?? 'created_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>(getSortDirectionParam() ?? 'desc');

  // 領収書発行タブで、チェックされたtrip_idを管理
  const [selectedTrips, setSelectedTrips] = useState<string[]>([]);
  const [allSelected, setAllSelected] = useState(false);

  // 4. tripParams の初期化
  const [tripParams, setTripPrams] = useState<TripParams>(() => {
    // 初期化する（実際の値はuseEffectで設定する）
    return {
      type: 'user' as Type,
      page: 1,
      trip_id: undefined,
      trip_from_date: undefined,
      trip_to_date: undefined,
      sort_key: 'created_at',
      sort_direction: 'desc',
      simple: 'true'
    };
  });
  const [showWorkflowUnappliedModal, setShowWorkflowUnappliedModal] = useState(false);
  const [workflowUnappliedTrips, setWorkflowUnappliedTrips] = useState<any[]>([]);
  const [inProgressTripIds, setInProgressTripIds] = useState<string[]>([]);

  // 処理中ステータスチェック
  const checkStatus = useCallback(() => {
    if (selectedKey !== 'receipt') return;

    const tripIdsToCheck = inProgressTripIds.length > 0 ? inProgressTripIds : undefined;
    Fetcher.get<{ in_progress_trip_ids: string[]; count: number; timestamp: string }>(
      '/trip_receipts/check_status.json',
      tripIdsToCheck ? { trip_ids: tripIdsToCheck } : {}
    )
      .then(result => {
        const prevCount = inProgressTripIds.length;
        const newCount = result.in_progress_trip_ids.length;

        setInProgressTripIds(result.in_progress_trip_ids);

        if (prevCount > 0 && newCount < prevCount) {
          setTimeout(() => {
            fetchTrips(false);
          }, 1000);
        }
      })
      .catch(e => {
        setError('通信環境が不安定です。\n時間をおいてもう一度お試しください。');
      });
  }, [selectedKey, inProgressTripIds]);

  const fetchTrips = useCallback(
    (isPolling = false) => {
      if (!isPolling) {
        setLoading(true);
      }
      setError(null);
      const endpoint = selectedKey === 'receipt' ? '/trip_receipts.json' : '/trips.json';
      StrictFetcher.get<FetchTripsResult>(endpoint, 'trips/index.json', tripParams)
        .then(result => {
          setOngoingTrips(_.map(result.trips, t => new Trip(convertTripResponseToArgs(t, result.show_fee))));
          setTotalPages(result.total_pages);
          if (selectedKey === 'receipt') {
            setInProgressTripIds(result.in_progress_trip_ids ?? []);
          }

          // 検索クエリをURLクエリに反映
          const queries = Object.entries(tripParams)
            .filter(([k, v]) => v !== undefined && !['type', 'simple'].includes(k))
            .map(([k, v]) => `${k}=${v}`)
            .join('&');
          window.history.replaceState(null, '', `${window.location.pathname}?${queries}`);
        })
        .finally(() => {
          if (!isPolling) {
            setLoading(false);
          }
        })
        .catch(e => {
          console.error(e);
          setError('通信環境が不安定です。\n時間をおいてもう一度お試しください。');
        });
    },
    [tripParams, selectedKey]
  );

  // fetchTripsの実行を制御するフラグ
  const [isInitialized, setIsInitialized] = useState(false);
  useEffect(() => {
    if (isInitialized && tripParams.page !== undefined) {
      fetchTrips();
    }
  }, [tripParams, isInitialized]);

  // ポーリング管理のuseEffect
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (selectedKey === 'receipt' && inProgressTripIds.length > 0) {
      // 処理中の旅程がある場合はポーリングを開始
      interval = setInterval(() => {
        checkStatus();
      }, POLLING_INTERVAL);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [selectedKey, inProgressTripIds.length, checkStatus]);

  // 処理完了を検知してリフレッシュ
  const [previousInProgressCount, setPreviousInProgressCount] = useState(0);
  useEffect(() => {
    if (selectedKey === 'receipt') {
      if (previousInProgressCount > 0 && inProgressTripIds.length < previousInProgressCount) {
        setTimeout(() => {
          fetchTrips(false);
        }, 1000);
      }
      setPreviousInProgressCount(inProgressTripIds.length);
    }
  }, [inProgressTripIds.length, previousInProgressCount, selectedKey, fetchTrips]);

  // tripParamsの更新を1回だけ行うためのフラグ
  const isUpdatingParams = useRef(false);
  useEffect(() => {
    // 既に更新中の場合はスキップ
    if (isUpdatingParams.current) {
      return;
    }

    isUpdatingParams.current = true;
    setCurrentPage(1);
    // URLパラメータを取得
    const urlTripId = getParam('trip_id');
    const urlFromDate = getParam('trip_from_date');
    const urlToDate = getParam('trip_to_date');
    const urlSortKey = (getParam('sort_key') as SortKey) || 'created_at';
    const urlSortDirection = (getParam('sort_direction') as SortDirection) || 'desc';
    const effectiveTripId = urlTripId || tripId || null;

    if (selectedKey === 'receipt') {
      // 領収書タブの場合
      const defaultFromDate = moment().subtract(2, 'months').startOf('day');
      const defaultToDate = moment().endOf('day');
      const newFromDate = urlFromDate ? moment(urlFromDate) : defaultFromDate;
      const fromDateString = urlFromDate || defaultFromDate.format('YYYY-MM-DD');
      const newToDate = urlToDate ? moment(urlToDate) : defaultToDate;
      const toDateString = urlToDate || defaultToDate.format('YYYY-MM-DD');
      // 状態を更新
      setTripId(effectiveTripId);
      setTripFromDate(newFromDate);
      setTripToDate(newToDate);
      setSortKey(urlSortKey);
      setSortDirection(urlSortDirection);
      setTripPrams({
        type: 'user',
        page: 1,
        trip_id: effectiveTripId || undefined,
        trip_from_date: fromDateString,
        trip_to_date: toDateString,
        sort_key: urlSortKey,
        sort_direction: urlSortDirection,
        simple: 'true'
      });
    } else {
      // 領収書タブ以外の場合
      const newFromDate = urlFromDate ? moment(urlFromDate) : undefined;
      const fromDateString = urlFromDate || undefined;
      const newToDate = urlToDate ? moment(urlToDate) : undefined;
      const toDateString = urlToDate || undefined;
      // 状態を更新
      setTripId(effectiveTripId);
      setTripFromDate(newFromDate);
      setTripToDate(newToDate);
      setSortKey(urlSortKey);
      setSortDirection(urlSortDirection);
      setTripPrams({
        type: 'user',
        page: 1,
        trip_id: effectiveTripId || undefined,
        trip_from_date: fromDateString,
        trip_to_date: toDateString,
        sort_key: urlSortKey,
        sort_direction: urlSortDirection,
        simple: 'true'
      });
    }

    // タブ切り替え時はチェック状態もリセット
    setSelectedTrips([]);
    setAllSelected(false);

    // 初期化完了を設定
    if (!isInitialized) {
      setIsInitialized(true);
    }

    // 更新完了後にフラグをリセット（次回のタブ切り替えに備える）
    setTimeout(() => {
      isUpdatingParams.current = false;
    }, 100);
  }, [selectedKey]);

  useEffect(() => {
    const unappliedTrips = ongoingTrips.filter(trip => trip.status === 8 && trip.freeeApprovalRequestId);
    setWorkflowUnappliedTrips(unappliedTrips);
    setShowWorkflowUnappliedModal(unappliedTrips.length > 0);
  }, [ongoingTrips]);

  const handleSearchTrips = useCallback(() => {
    setCurrentPage(1);
    setTripPrams({
      type: 'user',
      page: 1,
      trip_id: tripId || undefined,
      trip_from_date: tripFromDate?.format('YYYY-MM-DD'),
      trip_to_date: tripToDate?.format('YYYY-MM-DD'),
      sort_key: sortKey,
      sort_direction: sortDirection,
      simple: 'true'
    });

    // 検索ボタン押下時にチェックリセット
    setSelectedTrips([]);
    setAllSelected(false);
  }, [tripId, tripFromDate, tripToDate, sortKey, sortDirection]);

  const handlePageChange = useCallback(
    (_: React.ChangeEvent<unknown>, page: number) => {
      setCurrentPage(page);
      setTripPrams({
        type: 'user',
        page,
        trip_id: tripId || undefined,
        trip_from_date: tripFromDate?.format('YYYY-MM-DD'),
        trip_to_date: tripToDate?.format('YYYY-MM-DD'),
        sort_key: sortKey,
        sort_direction: sortDirection,
        simple: 'true'
      });
    },
    [tripId, tripFromDate, tripToDate, sortKey, sortDirection]
  );

  useEffect(() => {
    // ページが変わったらチェック状態をリセット
    setSelectedTrips([]);
    setAllSelected(false);
  }, [currentPage]);

  useEffect(() => {
    const selectableTrips = ongoingTrips
      .filter(trip => {
        const tripId = `${trip.id}`;
        const isInProgress = inProgressTripIds.includes(tripId);
        return trip.canIssueReceipt && !isInProgress;
      })
      .map(trip => `${trip.id}`);

    setAllSelected(selectableTrips.length > 0 && selectableTrips.every(id => selectedTrips.includes(id)));
  }, [selectedTrips, ongoingTrips, inProgressTripIds]);

  // 一括選択・解除
  const handleSelectAll = () => {
    setSelectedTrips(prevSelected => {
      const selectableTrips = ongoingTrips
        .filter(trip => {
          const tripId = `${trip.id}`;
          return trip.canIssueReceipt && !inProgressTripIds.includes(tripId);
        })
        .map(trip => `${trip.id}`);
      const selectedCount = prevSelected.filter(id => selectableTrips.includes(id)).length;
      return selectedCount === selectableTrips.length ? [] : selectableTrips;
    });
  };

  // チェックボックスの変更
  const handleSelectTrip = (selectedTripId: string) => {
    const isInProgress = inProgressTripIds.includes(selectedTripId);
    if (isInProgress) return;

    setSelectedTrips(prev => {
      const newSelection = prev.includes(selectedTripId)
        ? prev.filter(id => id !== selectedTripId)
        : [...prev, selectedTripId];
      return newSelection;
    });
  };

  return (
    <Wrap>
      <BodyWrap>
        <div>
          <TripHeader
            selected={selectedKey}
            tripReportAvailable={tripReportAvailable}
            tripReceiptAvailable={tripReceiptAvailable}
          />
          <Body>
            <Box display="flex">
              <PageTitle style={{ marginRight: 'auto' }}>
                {selectedKey === 'receipt' ? '領収書発行対象の旅程' : '予約した旅程一覧'}
              </PageTitle>
            </Box>
            <SearchFields className="searchFields">
              <SearchField>
                <SearchTitle>旅程番号</SearchTitle>
                <SearchInput>
                  <input
                    type="text"
                    placeholder="旅程番号"
                    value={tripId || ''}
                    onChange={e => setTripId(e.target.value)}
                  />
                </SearchInput>
              </SearchField>
              <SearchField>
                <SearchTitle>出張日</SearchTitle>
                <SearchInput>
                  <DatetimePicker
                    dateFieldId="filterParams-fromDate"
                    value={tripFromDate}
                    onChange={date => setTripFromDate(date)}
                    onClear={() => setTripFromDate(undefined)}
                    placeholder="開始日"
                    border
                    showToday
                    showPast
                    width="140px"
                    deletable
                  />
                  <span>〜</span>
                  <DatetimePicker
                    dateFieldId="filterParams-toDate"
                    value={tripToDate}
                    onChange={date => setTripToDate(date)}
                    onClear={() => setTripToDate(undefined)}
                    placeholder="終了日"
                    border
                    showToday
                    showPast
                    width="140px"
                    deletable
                  />
                </SearchInput>
              </SearchField>
              <SearchField>
                <SearchTitle>並び順</SearchTitle>
                <Box display="flex">
                  <SearchInput>
                    <select value={sortKey} onChange={e => setSortKey(e.target.value as SortKey)}>
                      <option value="created_at">旅程作成日順</option>
                      <option value="started_at">出発日順</option>
                    </select>
                  </SearchInput>
                  <SearchInput>
                    <select
                      value={sortDirection}
                      onChange={e => setSortDirection(e.target.value as SortDirection)}
                    >
                      <option value="desc">降順</option>
                      <option value="asc">昇順</option>
                    </select>
                  </SearchInput>
                </Box>
              </SearchField>
              <SearchField>
                <Button className={loading ? 'disabled' : undefined} onClick={handleSearchTrips}>
                  検索
                </Button>
              </SearchField>
            </SearchFields>
            <TripsWrap>
              {loading ? (
                <Loading />
              ) : error ? (
                <Error>{error}</Error>
              ) : selectedDraft ? (
                <DraftSearch
                  trip={selectedDraft}
                  onClose={() => setSelectedDraft(null)}
                  availableRepos={availableRepos}
                />
              ) : ongoingTrips.length > 0 ? (
                <ListWrap>
                  {selectedKey === 'receipt' && (
                    <Box
                      display="flex"
                      justifyContent="flex-start"
                      style={{ paddingLeft: '16px', gap: '10px' }}
                      marginBottom={1}
                    >
                      <Checkbox checked={allSelected} onChange={handleSelectAll} />
                      <Button
                        disabled={selectedTrips.length === 0}
                        onClick={() => {
                          if (selectedTrips.length > 0) {
                            history.push({
                              pathname: '/trip_receipts/new',
                              search: `?trip_ids=${selectedTrips.join(',')}`,
                              state: { referrer: `${window.location.pathname}${window.location.search}` }
                            });
                          }
                        }}
                      >
                        選択した旅程の領収書を発行
                      </Button>
                    </Box>
                  )}
                  {ongoingTrips.map(trip => (
                    <CardWrap key={`${trip.id}`}>
                      {selectedKey === 'receipt' ? (
                        <TripReceiptRow
                          receiptRecord={{
                            id: trip.id.toString(),
                            invoice_id: trip.invoiceId || 0,
                            cache_start_time: trip.cacheStartTime?.format('YYYY-MM-DD HH:mm:ss') || null,
                            cache_end_time: trip.cacheEndTime?.format('YYYY-MM-DD HH:mm:ss') || null,
                            departure: trip.departureLocation || trip.departure || null,
                            destination: trip.destination || trip.destinationText || null,
                            created_at: trip.createdAt.format('YYYY-MM-DD HH:mm:ss') || null,
                            total_price: trip.receiptTotalPrice || 0,
                            downloaded_count: trip.downloadedCount || 0,
                            downloaded_at: trip.downloadedAt
                              ? trip.downloadedAt.format('YYYY-MM-DD HH:mm:ss')
                              : null,
                            receipt_revision_status: trip.receiptRevisionStatus || 'none',
                            can_issue_receipt: trip.canIssueReceipt || false
                          }}
                          selectedTrips={selectedTrips}
                          inProgressTripIds={inProgressTripIds}
                          onSelectTrip={handleSelectTrip}
                          isDisabled={inProgressTripIds.includes(`${trip.id}`)}
                          referrer={`${window.location.pathname}${window.location.search}`}
                        />
                      ) : (
                        <TripRow trip={trip} selectedKey={selectedKey} />
                      )}
                    </CardWrap>
                  ))}
                  <Pagination
                    style={{ margin: '10px auto', width: 'fit-content' }}
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                  />
                </ListWrap>
              ) : (
                <NoItem>条件に合致する旅程はありません</NoItem>
              )}
            </TripsWrap>
          </Body>
        </div>
        <Modal open={showWorkflowUnappliedModal} onClose={() => setShowWorkflowUnappliedModal(false)}>
          <ModalHeader>ワークフローを申請してください</ModalHeader>
          <ModalBody>
            <Box marginBottom="20px">
              以下の旅程のワークフローが申請待ちになっています。
              <br />
              詳細ページに遷移してワークフローを申請してください。
            </Box>
            <Box>
              {workflowUnappliedTrips.map(trip =>
                trip.freee_approval_request_id ? (
                  <Box>
                    <a
                      href={`https://secure.freee.co.jp/approval_requests/${trip.freee_approval_request_id}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      freeeワークフロー申請ページ（旅程番号：{trip.id}）
                    </a>
                  </Box>
                ) : null
              )}
            </Box>
          </ModalBody>
        </Modal>
      </BodyWrap>
    </Wrap>
  );
});

export const Wrap = styled.div`
  min-height: 100vh;
`;

export const BodyWrap = styled.div`
  min-height: 100vh;
  display: block;
`;

export const Body = styled(ContentBody)`
  max-width: 1150px;
  padding: 20px;
  background: #fff;
`;

const PageTitle = styled.h2`
  font-size: 15px;
  font-weight: bold;
  margin-bottom: 24px;
`;

const SearchFields = styled.div`
  display: flex;
  column-gap: 16px;
  row-gap: 8px;
  align-items: flex-end;

  ${media.sp`
    flex-wrap: wrap;
  `}
`;

const SearchField = styled.div`
  display: flex;
  flex-flow: column;
`;

const SearchTitle = styled.label`
  font-size: 12px;
  color: ${props => props.theme.grayTextColor};
`;

const SearchInput = styled.div`
  flex-grow: 1;
  display: flex;
  align-items: center;
  column-gap: 4px;

  input[type='text'] {
    margin-bottom: 0;
  }

  label {
    font-weight: normal;
    cursor: pointer;
    margin-right: 10px;
  }

  input[type='checkbox'] {
    cursor: pointer;
  }

  & .single-calendar-input {
    width: 120px !important;
  }

  select,
  #filterParams-fromDate,
  #filterParams-toDate {
    background-color: #fff;
    border: 1px solid #eee;
    border-radius: 3px;
    box-shadow: inset 0 1px 3px rgb(0 0 0 / 6%);
    box-sizing: border-box;
    font-family: 'Helvetica Neue', 'Helvetica', 'Roboto', 'Arial', sans-serif;
    font-size: 1em;
    padding: 0.5em;
    margin-bottom: 0;
    transition: border-color;
    width: 100%;
    height: auto;
  }

  select {
    font-size: 14px;
  }
`;

export const LoadingWrap = styled.div`
  padding-top: 20px;
`;

const Error = styled.div`
  padding: 20px;
  color: ${props => props.theme.redColor};
`;

const TripsWrap = styled.div`
  padding: 20px 0;
`;
const ListWrap = styled.div``;

const NoItem = styled.p`
  margin: 0 auto;
  text-align: center;
`;
const CardWrap = styled.div`
  box-shadow: 0 0 3px 1px ${props => props.theme.grayBorderColor};
  border-radius: 5px;
`;

export default Trips;
